import { Dropdown } from 'primereact/dropdown';
import styled, { css } from 'styled-components';
import {
  borderRadius,
  borders,
  colourPalette,
  fontSize,
  fontWeight,
  gap,
  hexToRGBA,
  lineHeight,
  padding,
  shadow,
} from 'styles/styles';

export const PDropdownBaseStyle = css`
  &.p-dropdown {
    border-radius: ${borderRadius.roundedSM};
    background-color: ${colourPalette.neutral0};
    border: ${borders.borderSM} solid ${colourPalette.gray200};
    display: flex;
    align-items: center;
    min-height: 40px;

    .p-dropdown-label {
      padding: 0 ${gap.gap5} 0 ${gap.gap3};
      max-height: fit-content;
      font-size: ${fontSize.fontSizeSM};
      line-height: ${lineHeight.lineHeightMD};
      font-family: 'SuisseIntl', sans-serif;
      color: ${colourPalette.neutral600};
      font-weight: ${fontWeight.regular};
      &.p-placeholder {
        color: ${colourPalette.neutral400};
        font-weight: ${fontWeight.regular};
      }
      &::placeholder {
        color: ${colourPalette.neutral400};
        font-weight: ${fontWeight.regular};
      }
    }

    .p-dropdown-clear-icon {
      width: 16px;
      height: 16px;
      right: 38px;
      path {
        &[fill] {
          fill: ${colourPalette.neutral600};
        }
        &[stroke] {
          stroke: ${colourPalette.neutral600};
        }
      }
    }

    .p-dropdown-trigger {
      width: fit-content;
      padding: 0 ${gap.gap3} 0 ${gap.gap2};
      & > svg {
        width: 16px;
        height: 16px;
        path {
          &[fill] {
            fill: ${colourPalette.neutral600};
          }
          &[stroke] {
            stroke: ${colourPalette.neutral600};
          }
        }
      }
      &[aria-expanded='true'] {
        & > svg {
          transform: rotate(180deg);
        }
      }
    }

    &:hover {
      border-color: ${colourPalette.gray300};
      background-color: ${colourPalette.neutral100};
    }
    &:focus,
    &.p-inputwrapper-focus,
    &:focus-within,
    &.p-invalid:focus-within {
      border-color: ${colourPalette.blue500};
      box-shadow: 0px 0px 0px 4px ${hexToRGBA(colourPalette.blue600, 0.3)};
      background-color: ${colourPalette.neutral0};
    }

    &.p-invalid {
      border-color: ${colourPalette.rose500};
      .p-dropdown-label {
        color: ${colourPalette.rose600};
        &.p-placeholder,
        &::placeholder {
          color: ${colourPalette.rose600};
        }
      }
      & + .p-dropdown-invalid-icon {
        display: block;
      }
    }

    &.p-disabled {
      opacity: 1;
      background-color: ${colourPalette.neutral50};
      border-color: ${colourPalette.gray200};
      .p-dropdown-label {
        color: ${colourPalette.neutral300};
        &.p-placeholder,
        &::placeholder {
          color: ${colourPalette.neutral300};
        }
      }
      .p-dropdown-trigger > svg > path {
        &[fill] {
          fill: ${colourPalette.neutral300};
        }
        &[stroke] {
          stroke: ${colourPalette.neutral300};
        }
      }
      & + .p-dropdown-invalid-icon {
        display: none;
      }
    }

    &.p-dropdown-sm,
    .p-dropdown-sm {
      min-height: 32px;
    }
    &.p-dropdown-lg,
    .p-dropdown-lg {
      min-height: 56px;
      font-size: 18px;
    }

    &.p-dropdown-type-inline {
      background-color: transparent;
      border-color: transparent;
      min-height: 32px;
      &:hover {
        border-color: transparent;
        background-color: ${colourPalette.neutral100};
      }
      &:focus,
      &.p-inputwrapper-focus,
      &:focus-within,
      &.p-invalid:focus-within {
        border-color: transparent;
        background-color: transparent;
      }
      &.p-invalid {
        border-color: ${colourPalette.rose500};
        .p-dropdown-label {
          color: ${colourPalette.rose600};
          &.p-placeholder,
          &::placeholder {
            color: ${colourPalette.rose600};
          }
        }
        & + .p-dropdown-invalid-icon {
          display: block;
        }
      }
      &.p-disabled {
        background-color: transparent;
        border-color: transparent;
      }
      &:not(.p-invalid):not(.p-dropdown-clearable) {
        .p-dropdown-label {
          padding-right: ${padding.paddingNone};
        }
      }
    }

    &.p-dropdown-filled {
      background-color: ${colourPalette.neutral100};
      border: ${borders.borderSM} solid ${colourPalette.neutral100};
      &:hover {
        background-color: ${colourPalette.neutral200};
        border-color: ${colourPalette.neutral200};
      }
      &:focus,
      &.p-inputwrapper-focus,
      &:focus-within,
      &.p-invalid:focus-within {
        border-color: ${colourPalette.neutral100};
        background-color: ${colourPalette.neutral100};
      }
      &.p-invalid {
        background-color: ${colourPalette.rose50};
        border-color: ${colourPalette.rose50};
      }
      &.p-disabled {
        background-color: ${colourPalette.neutral50};
        border-color: ${colourPalette.neutral50};
      }
    }
  }
`;

export const PDropdownContainerBaseStyle = css`
  position: relative;
  min-width: 0;
  & > .p-dropdown-invalid-icon {
    display: none;
    width: 16px;
    height: 16px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 38px;
    pointer-events: none;
    path {
      &[fill] {
        fill: ${colourPalette.rose500};
      }
      &[stroke] {
        stroke: ${colourPalette.rose500};
      }
    }
  }
`;

export const PDropdownStyle = {
  Container: styled.div`
    ${PDropdownContainerBaseStyle}
    &.fit-content {
      width: fit-content;
    }
  `,
  Dropdown: styled(Dropdown)`
    ${PDropdownBaseStyle}
  `,
};

export const PDropdownGlobalStyle = css`
  div.p-dropdown-panel.p-component {
    box-shadow: ${shadow.shadowMD};
    /**
      * Note: Prime React sets the panel min-width to the actual field width dinamically
      * This is to adjust the panel to be the same width of the field
      */
    width: 0;

    & .p-dropdown-header {
      padding: ${gap.gap2} !important;
      border-bottom: 1px solid ${colourPalette.neutral300} !important;
      .p-dropdown-filter {
        border-radius: ${borderRadius.roundedSM};
        padding: 0 ${gap.gap3} 0 32px;
        height: 40px;
        background-color: ${colourPalette.neutral0} !important;
        border: ${borders.borderSM} solid ${colourPalette.gray200};
        &::placeholder {
          color: ${colourPalette.neutral400};
        }
        &:hover {
          border-color: ${colourPalette.gray300} !important;
          background-color: ${colourPalette.neutral100} !important;
        }
        &:focus {
          box-shadow: 0px 0px 0px 4px ${hexToRGBA(colourPalette.blue600, 0.3)} !important;
          border-color: ${colourPalette.blue500} !important;
          background-color: ${colourPalette.neutral0} !important;
        }
      }
      .p-dropdown-filter-icon {
        right: auto;
        left: 12px;
        font-size: 15px;
        transform: translateY(-50%);
        margin: 0;
      }
    }

    .p-dropdown-items-wrapper {
      overflow: auto;
      ::-webkit-scrollbar-track {
        border-left: ${borders.borderSM} solid ${colourPalette.neutral300};
      }
      ::-webkit-scrollbar-thumb {
        background-color: ${colourPalette.neutral300};
      }
    }

    .p-dropdown-items {
      padding: ${padding.paddingXS};
      & > .p-dropdown-item {
        height: 40px;
        padding: ${padding.paddingNone} ${padding.paddingMD};
        border-radius: ${borderRadius.roundedSM};
        color: ${colourPalette.neutral600};
        font-weight: ${fontWeight.regular};
        overflow: hidden;
        text-overflow: ellipsis;
        display: flex;
        align-items: center;
        outline: none;

        .p-dropdown-item-label {
          flex: 1;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        &:active {
          background-color: ${colourPalette.neutral0};
          box-shadow: 0px 0px 0px 2px ${hexToRGBA(colourPalette.blue600, 0.3)};
          position: relative;
          z-index: 1;
        }

        &.p-highlight {
          background-color: ${colourPalette.blue50};
          color: ${colourPalette.blue500};
        }
        &:hover {
          background-color: ${colourPalette.neutral100};
          color: ${colourPalette.neutral600};
        }
      }

      & > .p-dropdown-empty-message {
        height: 36px;
        padding-top: 0;
        padding-bottom: 0;
        display: flex;
        align-items: center;
      }

      & > .p-dropdown-item-group {
        padding: ${padding.paddingSM} ${padding.paddingMD} 0;
        height: 36px;
        display: flex;
        align-items: center;
        margin: 0 0 ${gap.gap2} 0;
        font-size: ${fontSize.fontSizeXS};
        font-weight: ${fontWeight.medium};
        line-height: ${lineHeight.lineHeightSM};
        position: relative;
        color: ${colourPalette.neutral400};
        margin-bottom: 0px;
        &:not(:first-child) {
          border-top: ${borders.borderSM} solid ${colourPalette.gray300};
          margin-top: ${gap.gap2} !important;
        }
      }
    }

    .p-dropdown-header {
      padding: ${padding.paddingXXS} ${padding.paddingXXS} 0;
      border-bottom: none;

      & .p-inputtext {
        padding-top: 0px;
        padding-bottom: 0px;
        min-height: 36px;
        font-size: ${fontSize.fontSizeSM};
        line-height: ${lineHeight.lineHeightMD};
        background-color: ${colourPalette.neutral0};
        border: ${borders.borderSM} solid ${colourPalette.gray200};
        &:hover {
          border-color: ${colourPalette.gray300};
        }
        &.p-invalid {
          border-color: ${colourPalette.rose500};
        }
        &:focus,
        &.p-invalid:focus {
          box-shadow: 0px 0px 0px 4px ${hexToRGBA(colourPalette.blue600, 0.3)};
          border-color: ${colourPalette.blue500};
        }
      }
    }
  }

  .header-items-no-padding.p-dropdown-panel .p-dropdown-items .p-dropdown-item-group {
    padding: 0px;
  }
  .fixed-width.p-dropdown-panel {
    width: 300px !important;
  }
`;
